import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useWelcomeMessages } from '@/hooks/useWelcomeMessages';
import { WelcomeMessageModal } from '@/components/WelcomeMessageModal';
import { useOnboardingTourContext } from '@/contexts/OnboardingTourContext';
import type { WelcomeMessage } from '@/hooks/useWelcomeMessages';

interface WelcomeMessagesContextValue {
  currentMessage: WelcomeMessage | null;
  isModalOpen: boolean;
  closeModal: () => void;
  dismissCurrentMessage: () => void;
}

const WelcomeMessagesContext = createContext<WelcomeMessagesContextValue | null>(null);

export function useWelcomeMessagesContext() {
  const context = useContext(WelcomeMessagesContext);
  if (!context) {
    throw new Error('useWelcomeMessagesContext must be used within WelcomeMessagesProvider');
  }
  return context;
}

interface WelcomeMessagesProviderProps {
  children: React.ReactNode;
}

export function WelcomeMessagesProvider({ children }: WelcomeMessagesProviderProps) {
  const location = useLocation();
  const { currentMessage, trackView, dismissMessage, isLoading } = useWelcomeMessages();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasShownForSession, setHasShownForSession] = useState<Set<string>>(new Set());
  const [displayedMessage, setDisplayedMessage] = useState<WelcomeMessage | null>(null);

  // Get onboarding tour state to coordinate modal display
  const { shouldShowTour, isLoading: isTourLoading } = useOnboardingTourContext();

  // Only show on dashboard after a delay
  const isDashboard = location.pathname === '/dashboard';

  useEffect(() => {
    // Only show on dashboard
    if (!isDashboard) {
      setIsModalOpen(false);
      return;
    }

    // Wait for tour loading to complete before deciding
    if (isTourLoading) {
      return;
    }

    // Don't show welcome message if onboarding tour is active
    // Tour takes priority as a first-time user experience
    if (shouldShowTour) {
      return;
    }

    // Don't show if loading or no message
    if (isLoading || !currentMessage) {
      return;
    }

    // For "always" mode, check session tracking
    // For "dismissable" mode, the hook already filters out dismissed messages
    if (currentMessage.display_mode === 'always') {
      if (hasShownForSession.has(currentMessage.id)) {
        return;
      }
    }

    // Delay showing the modal to let the dashboard load first
    const timer = setTimeout(() => {
      setDisplayedMessage(currentMessage);
      setIsModalOpen(true);
      
      // Track for session if always-show
      if (currentMessage.display_mode === 'always') {
        setHasShownForSession(prev => new Set(prev).add(currentMessage.id));
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [isDashboard, currentMessage, isLoading, hasShownForSession, shouldShowTour, isTourLoading]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const dismissCurrentMessage = useCallback(() => {
    if (displayedMessage) {
      dismissMessage(displayedMessage.id);
    }
    setIsModalOpen(false);
  }, [displayedMessage, dismissMessage]);

  const handleTrackView = useCallback(() => {
    if (displayedMessage) {
      trackView(displayedMessage.id);
    }
  }, [displayedMessage, trackView]);

  const value: WelcomeMessagesContextValue = {
    currentMessage: displayedMessage,
    isModalOpen,
    closeModal,
    dismissCurrentMessage,
  };

  return (
    <WelcomeMessagesContext.Provider value={value}>
      {children}
      <WelcomeMessageModal
        message={displayedMessage}
        isOpen={isModalOpen}
        onClose={closeModal}
        onDismiss={dismissCurrentMessage}
        onView={handleTrackView}
      />
    </WelcomeMessagesContext.Provider>
  );
}
